import { get, hostError, patch, post, type FailoverGroup, type GroupMember, type GroupParams, type MemberRole, type MemberState, type NodeInfo } from '../../api'
import { currentLang, t, type Key } from '../../i18n'
import type { IconName, State } from '../../ui'

/*
 * Node group (GROUP-UI, docs/architecture/node-group.md §4, §6, §8): the
 * operator sees the members, their states, shares, caps and the ramp; the
 * user never sees a member (the cabinet shows the group's name).
 *
 * Owner's answers to the stage-0 questions (30.10, AUDIT §17 NG-01…NG-08):
 * member states are new words on the system's idle / start looks (NG-01 A);
 * weights are mono numbers with an AUTO / MANUAL tag and the ramp a 10-segment
 * meter (NG-02 A); the group is a header row in Nodes with a page of its own
 * (NG-06 A).
 */

export const groupPath = (id: string) => `/admin/network/groups/${encodeURIComponent(id)}`
export const groupQueryKey = (id: string) => ['failover-group', id] as const

export type { BalanceEntry, BalanceKind, BalancePage, GroupMemberView, GroupView, MemberLoad, RehomeResult } from './groupTypes'
import type { BalanceEntry, BalanceKind, BalancePage, GroupMemberView, GroupView, RehomeResult } from './groupTypes'

export interface TOTP {
  totp?: string
}

/** PATCH …/group-member (GROUP-2a, GEG-1a): the membership roles and each role's weight and cap. */
export interface MemberPatch {
  member_roles?: MemberRole[]
  weight_mode?: 'auto' | 'manual'
  weight_pct?: number | null
  cap_pct?: number | null
  egress_weight_mode?: 'auto' | 'manual'
  egress_weight_pct?: number | null
  egress_cap_pct?: number | null
}

/** The role a member action applies to (GEG-1a): all = "Drain fully" for a drain. */
export type ActionRole = MemberRole | 'all'
export type MemberAction = 'drain' | 'return' | 'maintenance' | 'serve'

export const memberPath = (node: string) => `/api/admin/nodes/${encodeURIComponent(node)}/group-member`

export const groupsApi = {
  get: (id: string) => get<GroupView>(`/api/admin/failover/groups/${encodeURIComponent(id)}`),
  member: (node: string, body: MemberPatch & TOTP) => patch<{ node: string; member: GroupMember }>(memberPath(node), body),
  /** GEG-1a: drain, return, maintenance, serve per role (second factor) */
  action: (node: string, action: MemberAction, body: { role?: ActionRole; deadline_s?: number } & TOTP) =>
    post<{ node: string; roles?: MemberRole[]; state?: MemberState; deadline?: string }>(`${memberPath(node)}/${action}`, body),
  params: (id: string, params: Partial<GroupParams>, body: TOTP) => patch<FailoverGroup>(`/api/admin/failover/groups/${encodeURIComponent(id)}`, { params, ...body }),
  privateAddr: (node: string, addr: string) => patch<NodeInfo>(`/api/admin/nodes/${encodeURIComponent(node)}`, { private_addr: addr }),
  /** the balance journal, newest first (GROUP-3) */
  balance: (id: string, kind: BalanceKind | '', before?: number) => {
    const q = new URLSearchParams({ limit: String(BALANCE_PAGE) })
    if (kind) q.set('kind', kind)
    if (before) q.set('before', String(before))
    return get<BalancePage>(`/api/admin/failover/groups/${encodeURIComponent(id)}/balance?${q}`)
  },
  /** move a running broadcast's outputs (every platform reconnects): target n<id> or "center" */
  rehome: (route: string, target: string, totp?: string) =>
    post<RehomeResult>(`/api/admin/relay/routes/${encodeURIComponent(route)}/rehome`, { target, confirm: REHOME_CONFIRM, totp }),
}

export const BALANCE_PAGE = 50
export const balanceQueryKey = (id: string, kind: string) => ['failover-group', id, 'balance', kind] as const
/** The API's word of a rehome: the operator confirmed that every platform reconnects. */
export const REHOME_CONFIRM = 'platforms_reconnect'

/** A node group (named <region>-<index>), not a region group of C0 or a cluster. */
export const isNodeGroup = (g?: Pick<FailoverGroup, 'kind' | 'region'>) => !!g && g.kind === 'group' && !!g.region

export type MemberLook = 'joining' | 'serving' | 'draining' | 'drained' | 'maintenance' | 'down'

const LOOK: Record<MemberLook, { state: State; word: Key; hint: Key }> = {
  joining: { state: 'start', word: 'group.state.joining', hint: 'group.hint.joining' },
  serving: { state: 'live', word: 'group.state.serving', hint: 'group.hint.serving' },
  draining: { state: 'idle', word: 'group.state.draining', hint: 'group.hint.draining' },
  drained: { state: 'idle', word: 'group.state.drained', hint: 'group.hint.drained' },
  maintenance: { state: 'idle', word: 'group.state.maintenance', hint: 'group.hint.maintenance' },
  down: { state: 'fail', word: 'group.state.down', hint: 'group.hint.down' },
}

/** The stored state ('' = serving): the ingest role's, or the egress role's of an egress-only member. */
export const storedState = (m: Pick<GroupMember, 'state'>): Exclude<MemberLook, 'down'> => (m.state || 'serving') as Exclude<MemberLook, 'down'>

/* ── membership roles (GEG-1a, node-group.md §5.5.2; GEU-01…GEU-05) ── */

type RoleFields = Pick<GroupMember, 'roles' | 'state' | 'ingest_state' | 'egress_state'>

/** A member's membership roles: an older center sends none — then it only takes encoders. */
export const rolesOf = (m: Pick<GroupMember, 'roles'>): MemberRole[] => (m.roles?.length ? m.roles : ['ingest'])
export const hasRole = (m: Pick<GroupMember, 'roles'>, r: MemberRole) => rolesOf(m).includes(r)
export const isEgressOnly = (m: Pick<GroupMember, 'roles'>) => !hasRole(m, 'ingest')

/** One role's stored state (undefined: the member has no such role). */
export function roleState(m: RoleFields, r: MemberRole): MemberState | undefined {
  if (!hasRole(m, r)) return undefined
  if (r === 'ingest') return m.ingest_state ?? ((m.state || 'serving') as MemberState)
  return m.egress_state ?? 'serving'
}

/** The member's states, role by role, in the order ingest, egress. */
export const roleStates = (m: RoleFields): { role: MemberRole; state: MemberState }[] =>
  rolesOf(m).map((role) => ({ role, state: roleState(m, role)! }))

/** The look of one stored state (no Health overlay: DOWN is one chip per member, GEU-03). */
export function stateLook(st: MemberState): { state: State; word: string } {
  const l = LOOK[st]
  return { state: l.state, word: t(l.word) }
}

/** Health's failure on the member (both roles, GEU-03: one fail chip); maintenance is not DOWN. */
export const isDown = (m: RoleFields & Pick<GroupMemberView, 'health'>) =>
  !!m.health && m.health !== 'serving' && !roleStates(m).every((r) => r.state === 'maintenance')

const ACTIVE = new Set<MemberState>(['joining', 'serving'])
const BACK = new Set<MemberState>(['draining', 'drained', 'maintenance'])

/** The roles a drain may take now (joining / serving), the ones a return brings back. */
export const drainable = (m: RoleFields) => roleStates(m).filter((r) => ACTIVE.has(r.state)).map((r) => r.role)
export const returnable = (m: RoleFields) => roleStates(m).filter((r) => BACK.has(r.state)).map((r) => r.role)

/** The API's role for the roles of an action: one role, or all when every role of the member is in it. */
export function actionRole(m: Pick<GroupMember, 'roles'>, roles: MemberRole[]): ActionRole {
  return roles.length > 1 || (roles.length === rolesOf(m).length && rolesOf(m).length > 1) ? 'all' : (roles[0] ?? 'ingest')
}

/**
 * What the chip says: Health's failure laid over the stored state (§4.1, failed is not stored);
 * the stored state then goes in muted text next to it. A member in maintenance that the
 * provisioner sees off is not DOWN: going down is what maintenance is for.
 */
export function memberLook(m: Pick<GroupMemberView, 'state' | 'health'>): { look: MemberLook; state: State; word: string; hint: string; was?: string } {
  const stored = storedState(m)
  const down = !!m.health && m.health !== 'serving' && stored !== 'maintenance'
  const look: MemberLook = down ? 'down' : stored
  const l = LOOK[look]
  return { look, state: l.state, word: t(l.word), hint: t(l.hint, { n: 0 }), was: down ? t(LOOK[stored].word) : undefined }
}

/** Step of the ramp 1–10 from the factor (0 with a manual ramp). */
export const rampStep = (ramp: number) => Math.max(0, Math.min(10, Math.round(ramp * 10)))

/** .sx-load: magnitude, never green; ≥ 80 % warn, ≥ 95 % fail (ConsoleNodeTable). */
export function loadState(load: number): '' | 'warn' | 'fail' {
  const p = load * 100
  return p >= 95 ? 'fail' : p >= 80 ? 'warn' : ''
}

/** Percent in mono with a no-break space: "41 %" (04 numbers); one decimal under 10. */
export function pct(v: number | undefined): string {
  if (v === undefined || Number.isNaN(v)) return '—'
  const r = v < 10 && v > 0 && Math.round(v) !== v ? v.toFixed(1) : String(Math.round(v))
  return `${r} %`
}

/**
 * Which actions a member offers now (§4.2, GEG-1a per role): end ramp while the ingest role joins,
 * drain while a role is joining / serving (GEU-05: one button, the role chosen in the dialog),
 * maintenance when every role is drained, return while a role is draining / drained / in maintenance.
 */
export function memberActions(m: RoleFields): MemberAction[] {
  const out: MemberAction[] = []
  if (roleState(m, 'ingest') === 'joining') out.push('serve')
  if (drainable(m).length) out.push('drain')
  const rs = roleStates(m)
  if (rs.every((r) => r.state === 'drained')) out.push('maintenance')
  if (returnable(m).length) out.push('return')
  return out
}

/**
 * An error answer of the group endpoints, in words (09-voice; the raw text only as the
 * last resort, behind "The center did not take it:"). Code errors (totp_*) are the dialog's.
 */
export function groupErrorText(e: unknown, ctx: { name?: string; share?: number; cap?: number } = {}): string {
  const h = hostError(e)
  const raw = h?.error ?? (e as Error)?.message ?? ''
  const name = ctx.name ?? ''
  if (h?.code === 'provisioner_unavailable' || h?.code === 'no_provisioner') return t('group.err.prov')
  // GEG-1a: per-role refusals, by code first
  if (h?.code === 'role_not_drained') return t('group.err.roleNotDrained', { name })
  if (h?.code === 'member_roles_managed') return t('group.err.managed', { name })
  if (h?.code === 'drain_order') return t('group.err.agentOff', { name })
  if (h?.code === 'member_state') {
    const r = /the (ingest|egress) role is (\w+); (\w+) needs ([\w ,|]+)/.exec(raw)
    if (r) return t('group.err.roleState', { name, role: roleWord(r[1] as MemberRole), now: stateWord(r[2]!), needed: r[4]!.split(/[ ,|]+|\bor\b/).filter((x) => x in LOOK).map(stateWord).join(' / ') })
    const mt = /maintenance needs every role drained; the (ingest|egress) role is (\w+)/.exec(raw)
    if (mt) return t('group.err.maintRoles', { name, role: roleWord(mt[1] as MemberRole), now: stateWord(mt[2]!) })
    if (/changed meanwhile/.test(raw)) return t('group.err.changed', { name })
  }
  const nr = /the member has no (ingest|egress) role/.exec(raw)
  if (nr) return t('group.err.noRole', { name, role: roleWord(nr[1] as MemberRole) })
  if (/weight_pct above cap_pct/.test(raw)) return t('group.err.overCap', { share: ctx.share ?? '—', cap: ctx.cap ?? '—' })
  if (/manual weights add up/.test(raw)) return t('group.err.sum')
  if (/weight_pct: required|needs weight_pct/.test(raw)) return t('group.err.needShare')
  if (/weight_pct 0–100|cap_pct \(0, 100\]/.test(raw)) return t('group.err.pct')
  if (/^the member is (\w+); \w+ needs (\w+)/.test(raw)) {
    const m = /needs (\w+)/.exec(raw)!
    return t('group.err.state', { name, needed: t(LOOK[(m[1] as MemberLook) in LOOK ? (m[1] as MemberLook) : 'serving'].word) })
  }
  if (/changed meanwhile/.test(raw)) return t('group.err.changed', { name })
  if (/not a group member/.test(raw)) return t('group.err.notMember', { name })
  if (/egress_offload/.test(raw)) return t('group.err.offload')
  if (/ramp_s|drain_deadline_s/.test(raw)) return t('group.err.range')
  if (/private_addr/.test(raw)) return t('group.err.addr')
  if (/agent (is )?(not connected|offline)|host_offline|agent_offline/.test(raw) || h?.code === 'offline') return t('group.err.agentOff', { name })
  return t('group.err.other', { why: raw })
}

/** The role's word (GEU-14): Ingest / Outputs. */
export const roleWord = (r: MemberRole) => t(r === 'ingest' ? 'geg.role.ingest' : 'geg.role.egress')
/** The role's tag (GEU-03): INGEST / OUTPUTS. */
export const roleTag = (r: MemberRole) => t(r === 'ingest' ? 'geg.tag.ingest' : 'geg.tag.egress')
const stateWord = (st: string) => (st in LOOK ? t(LOOK[st as MemberLook].word) : st)

/* ── moving a running broadcast's outputs (GROUP-3 rehome, node-group.md §6.4) ── */

const REHOME_CODES = new Set([
  'too_young', 'moved_recently', 'target_busy', 'budget_spent', 'frozen', 'not_in_group', 'follows_main', 'same_hub',
  'target_not_in_group', 'target_unfit', 'no_target', 'route_ended', 'not_on_air', 'routes_elsewhere', 'confirm_required',
])

/** A refusal of a rehome in words, by its code; a wait adds "You can try in N min." from retry_after_s. */
export function rehomeErrorText(e: unknown, ctx: { to: string; group: string }): string {
  const h = hostError(e)
  if (!h?.code || !REHOME_CODES.has(h.code)) return groupErrorText(e)
  const budget = (e as { data?: { budget?: number } }).data?.budget ?? 1
  const text = t(`group.move.err.${h.code}` as Key, { to: ctx.to, group: ctx.group, budget })
  const wait = h.retry_after_s && h.retry_after_s > 0 ? Math.max(1, Math.ceil(h.retry_after_s / 60)) : 0
  return wait ? `${text} ${t('group.move.later', { n: wait })}` : text
}

/** Why a member cannot take a broadcast's outputs (undefined: it can). */
export function hubUnfit(m: GroupMemberView, route: { ingress: string; egress: string }): string | undefined {
  if (m.node === route.ingress) return t('group.move.why.ingress')
  if (m.node === route.egress) return t('group.move.why.same')
  if (!m.hub || !hasRole(m, 'egress')) return t('group.move.why.noHub')
  if (roleState(m, 'egress') !== 'serving' || (m.health && m.health !== 'serving')) return t('group.move.why.state')
  return undefined
}

/* ── the balance journal in words (NG-03 A: one member, one row, the reason) ── */

export interface BalanceLine {
  key: string
  at: string
  icon: IconName
  state?: State
  subject: string
  /** the subject links to a member's node card */
  node?: string
  text: string
}

const pctOf = (w: number, sum: number) => (sum > 0 ? Math.round((w * 100) / sum) : 0)
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
const dec = (v: number) => (currentLangDecimalComma() ? v.toFixed(2).replace('.', ',') : v.toFixed(2))

function currentLangDecimalComma() {
  return currentLang() !== 'en'
}

const STATE_WORD: Record<string, Key> = {
  joining: 'group.state.joining', serving: 'group.state.serving', draining: 'group.state.draining', drained: 'group.state.drained', maintenance: 'group.state.maintenance',
}
const STATE_LOOK: Record<string, State> = { joining: 'start', serving: 'live', draining: 'idle', drained: 'idle', maintenance: 'idle' }

function whyText(reason = ''): string {
  if (reason.startsWith('operator')) return t('group.log.why.operator')
  const k = ({ 'drain order': 'drain', 'ramp starts': 'rampStarts', 'ramp done': 'rampDone', undrain: 'undrain', drained: 'drained', 'back after a failure': 'back' } as Record<string, string>)[reason]
  return k ? t(`group.log.why.${k}` as Key) : reason
}

/**
 * The rows of one journal entry: a weights entry is one row per member whose share moved
 * (shares from the written DNS weights); the others are one row each.
 */
export function balanceLines(e: BalanceEntry, nameOf: (id?: string) => string, group: string): BalanceLine[] {
  const base = { at: e.at }
  const d = e.details ?? {}
  const operator = e.actor_id !== undefined
  switch (e.kind) {
    case 'weights': {
      const w = (d.weights ?? {}) as Record<string, number>
      const was = (d.was ?? {}) as Record<string, number>
      // streams: the members' live broadcasts (GROUP-FIX-1); load: rows written before it
      const streams = (d.streams ?? {}) as Record<string, number>
      const load = (d.load ?? {}) as Record<string, { load?: number; by?: string }>
      const sum = Object.values(w).reduce((a, b) => a + b, 0)
      const sumWas = Object.values(was).reduce((a, b) => a + b, 0)
      const out: BalanceLine[] = []
      for (const id of Object.keys(w).sort((a, b) => nameOf(a).localeCompare(nameOf(b)))) {
        const a = id in was ? pctOf(was[id]!, sumWas) : undefined
        const b = pctOf(w[id]!, sum)
        if (a === b) continue
        const l = load[id]
        const n = num(streams[id])
        const share = { a: a === undefined ? '—' : a, b }
        const text =
          e.reason === 'takers changed'
            ? t('group.log.share.takers', share)
            : n !== undefined
              ? t('group.log.share.streams', { ...share, n })
              : l && num(l.load) !== undefined
              ? t('group.log.share.load', { ...share, l: dec(l.load!), by: l.by ? t(`group.load.${l.by}` as Key) : '—' })
              : t('group.log.share', share)
        out.push({ ...base, key: `${e.id}-${id}`, icon: 'state-idle', subject: nameOf(id), node: id, text })
      }
      return out
    }
    case 'ramp_step': {
      const f = num(d.factor) ?? 0
      return [{ ...base, key: `${e.id}`, icon: 'state-start', state: 'start', subject: nameOf(e.node), node: e.node, text: t('group.log.ramp', { n: rampStep(f) }) }]
    }
    case 'cap':
      return [{ ...base, key: `${e.id}`, icon: d.held ? 'state-warn' : 'state-idle', state: d.held ? 'warn' : undefined, subject: nameOf(e.node), node: e.node, text: t(d.held ? 'group.log.capHeld' : 'group.log.capBelow') }]
    case 'member_state': {
      // GEG-1a: the membership roles changed (details.roles, details.was)
      if (Array.isArray(d.roles)) {
        const words = (x: unknown) => (Array.isArray(x) && x.length ? (x as MemberRole[]).map(roleWord).join(' + ') : '—')
        return [{ ...base, key: `${e.id}`, icon: 'user', subject: nameOf(e.node), node: e.node, text: t('geg.log.roles', { was: words(d.was), now: words(d.roles) }) }]
      }
      const from = String(d.from ?? '') || 'serving'
      const to = String(d.to ?? '') || 'serving'
      const words = { from: STATE_WORD[from] ? t(STATE_WORD[from]!) : from, to: STATE_WORD[to] ? t(STATE_WORD[to]!) : to, why: whyText(e.reason) }
      const role = d.role === 'ingest' || d.role === 'egress' ? (d.role as MemberRole) : undefined
      // GEG-1a rows name the role (details.role); rows before it don't
      const text = role
        ? t(words.why ? 'geg.log.stateWhy' : 'geg.log.state', { ...words, role: roleWord(role) })
        : t(words.why ? 'group.log.state.why' : 'group.log.state', words)
      return [{ ...base, key: `${e.id}`, icon: operator ? 'user' : `state-${STATE_LOOK[to] ?? 'idle'}` as IconName, state: operator ? undefined : STATE_LOOK[to], subject: nameOf(e.node), node: e.node, text }]
    }
    case 'offload':
      return [{ ...base, key: `${e.id}`, icon: 'user', subject: group, text: t(d.on ? 'group.log.offloadOn' : 'group.log.offloadOff') }]
    case 'egress_mode': {
      const mode = (x: unknown) => t(x === 'group' ? 'geg.mode.groupShort' : 'geg.mode.centerShort')
      return [{ ...base, key: `${e.id}`, icon: 'user', subject: group, text: t('geg.log.mode', { a: mode(d.was), b: mode(d.mode) }) }]
    }
    case 'outputs_moved': {
      const k = e.reason === 'egress_failed' ? 'geg.log.moved.failed' : e.reason === 'egress_drain' ? 'geg.log.moved.drain' : e.reason === 'hub_drain' ? 'group.log.moved.drain' : 'group.log.moved.operator'
      const failed = e.reason === 'egress_failed'
      return [{
        ...base, key: `${e.id}`, icon: operator ? 'user' : failed ? 'state-fail' : 'state-warn', state: operator ? undefined : failed ? 'fail' : 'warn', subject: e.stream ?? e.route ?? '—',
        text: t(k as Key, { from: nameOf(e.from), to: nameOf(e.to) }),
      }]
    }
    case 'hub_fallback': {
      const busy = e.reason === 'group_hubs_busy' || e.reason === 'group_egress_busy'
      const now = e.reason === 'group_egress_busy' || e.reason === 'no_group_egress'
      return [{
        ...base, key: `${e.id}`, icon: 'state-warn', state: 'warn', subject: e.stream ?? e.route ?? '—',
        text: t(now ? (busy ? 'geg.log.fallback.busy' : 'geg.log.fallback.none') : busy ? 'group.log.fallback.busy' : 'group.log.fallback.none', { to: nameOf(e.to) }),
      }]
    }
    // GEG-1b (GEU-11): the same-node route and its transitions; the platforms did not reconnect
    case 'local':
      return [{ ...base, key: `${e.id}`, icon: 'state-idle', subject: e.stream ?? e.route ?? '—', text: t('geg.log.local', { node: nameOf(e.to ?? e.node) }) }]
    case 'localize':
      return [{ ...base, key: `${e.id}`, icon: 'state-idle', subject: e.stream ?? e.route ?? '—', text: t('geg.log.localize', { node: nameOf(e.to ?? e.node) }) }]
    case 'reingress':
      return [{ ...base, key: `${e.id}`, icon: 'state-idle', subject: e.stream ?? e.route ?? '—', text: t('geg.log.reingress', { from: nameOf(e.from), to: nameOf(e.to) }) }]
  }
  return []
}
